import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {GROUP_KEYS, useGroupListQueryParams} from "@/api/groupApi";
import type {TTiktokKeywordPlanRead, TTiktokKeywordSelection} from "@/api/tiktokVisibilityApi";
import type {
  CampaignLocationSerializer,
  CampaignOwnerDetailSerializer,
  CreativeSnippetSerializer,
  UGCContentSerializer,
} from "@/generated/DjangoTypes";
import {useIsAgency, useSelectedCompany} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import {i18n} from "@/i18n";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {queryClient} from "@/queryClient";
import useCampaignManagementStore from "@/store/campaignManagement";
import {COUNTRY_CODE_TO_CAMPAIGN_LOCATION, COUNTRY_FILTERS, SUPPORTED_CAMPAIGN_COUNTRIES} from "@/store/constants";
import {create, type TSetter} from "@/store/createStore";
import {
  createCampaignForCompanyAtom,
  createCampaignFromGroupAtom,
  type TCreateCampaignFromGroup,
} from "@/store/jotaiAtoms";
import type {
  TCampaignCreationType,
  TCampaignStatus,
  TCountry,
  TSubscriptionWithDiscountOptions,
  TSupportedCampaignCountry,
} from "@/store/types";
import useUserStore from "@/store/user";
import {CAMPAIGN_SIZE_RANGES, getCampaignSizeOptions, getInstagramBonusPoolLimit} from "@/utils/campaignUtils";
import {
  asContentApprovalMode,
  asStoryApprovalMode,
  DEFAULT_AI_CONTENT_APPROVAL_THRESHOLD,
  type TContentApprovalMode,
  type TStoryApprovalMode,
} from "@/utils/contentApprovalMode";
import {handleErrorWithToast, type TError} from "@/utils/errorHandlers";
import urlify from "@/utils/urlify";
import {arrayMove} from "@dnd-kit/sortable";
import type {AxiosResponse} from "axios";
import dayjs from "dayjs";
import {getDefaultStore} from "jotai";
import {isEmpty} from "lodash";
import isEqual from "lodash/isEqual";
import sumBy from "lodash/sumBy";
import * as RPNInput from "react-phone-number-input";
import {toast} from "react-toastify";
import {z} from "zod";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";
import {useShallow} from "zustand/shallow";

export const MIN_CASTING_DAYS = 3;
export const MIN_CASTING_DAYS_SHORT_TERM = 1;
export const MAX_CASTING_DAYS = 14;
export const MIN_PREPARATION_DAYS = 5;
export const MIN_PREPARATION_DAYS_SHORT_TERM = 2;
export const MIN_PREPARATION_DAYS_CASH = 0;
export const MAX_PREPARATION_DAYS = 14;
export const MIN_ACTIVE_DAYS = 5;
export const MIN_ACTIVE_DAYS_SHORT_TERM = 3;
export const MAX_ACTIVE_DAYS = 30;
export const ACCOUNT_TAG_LIMIT = 20;
export const HASHTAG_LIMIT = 30;
export const TASK_CHARACTER_LIMIT = 1200;

export const EVENT_ACTIVE_DAYS = 3;
export const EVENT_PREPARATION_DAYS = 3;
export const ACTIVE_DAYS = 14;
const ACTIVE_DAYS_UGC = 7;
export const PREPARATION_DAYS = 7;
export const CASTING_DAYS = 10;

export const UGC_COST_PER_VIDEO_15_SECONDS = 79;
export const UGC_COST_PER_VIDEO_30_SECONDS = 99;
const UGC_CASH_REWARD_PERCENTAGE = 0.55;

export const BONUS_POOL_MIN_CASH_REWARD = 40;
export const BONUS_POOL_MIN_CASH_REWARD_NET = BONUS_POOL_MIN_CASH_REWARD * 0.8; // After backend deductions - primarily used in campaign overview
export const BONUS_POOL_MIN_REQUIRED_APPLICANTS = 3;

export const MARKDOWN_SYMBOLS_PATTERN = /[*_\-#~>\[\]]/g;

export const WORD_COUNT_DIFFERENCE_CREATIVE_SNIPPETS_SORTING = 13;

const parseAsNumber = (param: number | null) => {
  if (param === null) return -1;
  const parsed = Number(param);
  if (isNaN(parsed)) return -1;
  return parsed;
};

const getChangedTags = (prefix: string, newTag: string) => {
  const formattedValue = newTag
    .replaceAll(`${prefix} `, "")
    .replace(new RegExp(`${prefix}+`, "g"), prefix)
    .replace(new RegExp(`${prefix}(\\w+)(?=${prefix})`, "g"), `${prefix}$1 `)
    .replace(new RegExp(`${prefix}\\s${prefix}$`, "g"), prefix);

  return formattedValue.trim().split(/[\s,]+/);
};

const CampaignMiscStepOneSchema = z.object({
  brandDoes: z
    .string()
    .min(1, "product_description_required")
    .refine(value => value.replace(MARKDOWN_SYMBOLS_PATTERN, "").length <= 1000, {
      error: "product_description_max_length_error",
    }),
  title: z.string().min(1, "campaign_title_required"),
  sellingPrice: z.number().nullable().transform(parseAsNumber),
  cashReward: z.number().nullable().transform(parseAsNumber),
  longDescription: z.string().refine(value => value.replace(MARKDOWN_SYMBOLS_PATTERN, "").length <= 1000, {
    error: "campaign_description_max_length_error",
  }),
  locations: z.array(z.any()).min(1, "countries_min_length_error"),
  picture: z.preprocess(value => value ?? "", z.string().min(1, "campaign_image_required")),
});

const CampaignMiscStepThreeSchema = z.object({
  activeDays: z.number().min(MIN_ACTIVE_DAYS).max(MAX_ACTIVE_DAYS),
  castingDays: z.number().min(MIN_CASTING_DAYS).max(MAX_CASTING_DAYS),
  eventAddressDescription: z.string().min(1, "event_location_required"),
  preparationDays: z.number().min(MIN_PREPARATION_DAYS).max(MAX_PREPARATION_DAYS),
});

const CampaignInstagramSchema = z.object({
  accountTags: z
    .array(z.any())
    .min(1, "account_tag_required")
    .max(ACCOUNT_TAG_LIMIT, "account_tag_limit_reached")
    .refine(arr => arr.some(tag => tag.trim() !== "" && tag.trim() !== "@"), {error: "account_tag_required"}),
  hashtags: z.array(z.any()).max(HASHTAG_LIMIT, "hashtag_limit_reached"),
});

const CampaignTiktokSchema = z.object({
  accountTags: z.array(z.any()).max(ACCOUNT_TAG_LIMIT, "account_tag_limit_reached"),
  hashtags: z.array(z.any()).max(HASHTAG_LIMIT, "hashtag_limit_reached"),
});

const CampaignSocialSchemaStepTwo = z.object({
  campaignSize: z.string().min(1),
  task: z
    .string()
    .min(1, "task_required")
    .refine(value => value.replace(MARKDOWN_SYMBOLS_PATTERN, "").length <= TASK_CHARACTER_LIMIT, {
      error: "task_max_length_error",
    }),
});

const DigitalShopUrlSchema = z.object({
  digitalShopUrl: z.preprocess(value => value ?? "", z.string().min(1, "digital_shop_url_required")),
});

const SchemaCollection = z.object({
  ...CampaignInstagramSchema.shape,
  ...CampaignMiscStepOneSchema.shape,
  ...CampaignMiscStepThreeSchema.shape,
  ...CampaignSocialSchemaStepTwo.shape,
  ...CampaignTiktokSchema.shape,
  ...DigitalShopUrlSchema.shape,
});

export type TCampaignAccordionKey =
  | "additional_requirements"
  | "admin_only_options"
  | "amplifiers_step_1"
  | "amplifiers_step_2"
  | "campaign_brief"
  | "campaign_size"
  | "deliverables_platform_campaign_instagram"
  | "deliverables_platform_campaign_tiktok"
  | "describe_the_campaign"
  | "describe_the_product"
  | "time_frame"
  | "video_guide_number";

export type TCampaignSize = "5-10" | "10-15" | "20-50" | "50-100" | "100+";

type TFileUploadResponse = {
  id: number;
  image: string;
  thumbnail?: string | null;
};

type TState = {
  campaign: TComposedCampaign;
  formState: TFormState;
  actions: TActions;
};

export type TCampaignLocation = Partial<Omit<CampaignLocationSerializer, "id">>;

export type TContentLanguage = "Swiss German" | "High German" | "English" | "French" | "Italian";

export type TContentRequirements = {
  stories: number;
  posts: number;
  reels: number;
} | null;

export type TComposedCampaign = {
  misc: {
    activeDays: number | null;
    applicationPhaseOver: boolean;
    bonusPoolVersion: number;
    brandDoes: string;
    brandName: string;
    campaignLocations: TCampaignLocation[];
    campaignType: TCampaignCreationType;
    canCreateEvents?: boolean;
    cashReward: number | null;
    castingDays: number | null;
    castingEndDate: string; //same as 'deadline'
    castingStartDate: string;
    companyId?: number;
    companyImage?: string | null;
    digitalShopUrl?: string | null;
    endDate: string; //same as 'completed'
    event?: boolean;
    eventAddressCity?: string | null;
    eventAddressCountry?: string | null;
    eventAddressDescription?: string;
    eventAddressPlaceId?: string | null;
    eventAddressPostal?: string | null;
    eventAddressStreet?: string | null;
    eventAttendedByInfluencers?: boolean | null;
    eventDuration: "ONE_DAY" | "MULTIPLE_DAYS";
    eventEndDate?: string | null;
    eventStartDate?: string | null;
    generativeAiAllowed: boolean;
    hasChosenContentOption?: boolean;
    hasEditedCashReward?: boolean;
    hasEditedPrice?: boolean;
    id?: number;
    imageUploadProgress?: number | null;
    images: {image: string; id: number; thumbnail?: string | null}[];
    isBonusPoolActive?: boolean;
    aiContentApprovalThreshold: number;
    aiRecommendationsEnabled: boolean;
    isCash: boolean;
    contentApprovalCashFloorExempt: boolean;
    contentApprovalMode: TContentApprovalMode;
    contentApprovalLocked: boolean;
    storyApprovalMode: TStoryApprovalMode;
    hasCommunityCodes: boolean;
    isExtended: boolean;
    isLoadingSnippets: boolean;
    isOngoing: boolean;
    isProInfluencer: boolean;
    isProduct: boolean;
    isServiceClothing: boolean;
    isVoucher: boolean;
    locations: TCountry[];
    longDescription: string;
    mainImageUploadProgress?: number | null;
    manualScheduling: boolean;
    picture: string | null;
    preparationDays: number | null;
    preparationEndDate: string;
    privateForBrands?: boolean;
    privateForInfluencers?: boolean;
    productVariant: string;
    usesStructuredProductVariants: boolean;
    sellingPrice: number | null;
    skipCharacterLimits?: boolean;
    sortSnippetTriggerWordCount: number;
    sortedContentFormatSnippets: CreativeSnippetSerializer[];
    sortedMoodStyleSnippets: CreativeSnippetSerializer[];
    status: TCampaignStatus;
    title: string;
    ugcDontsDescription: string;
    wasBonusPoolActiveBeforeOngoing?: boolean;
  };
  instagram: {
    isLinkStickerEnabled: boolean;
    linkSticker: string | null;
    isLinkStickerTitleEnabled: boolean;
    linkStickerTitle?: string | null;
    requiredContentUploads: TContentRequirements;
    hashtags: string[];
    accountTags: string[];
    contentOptionOne?: TContentRequirements | null;
    contentOptionTwo?: TContentRequirements | null;
    instagramPartnershipAds: boolean;
  };
  socials: {
    campaignSize?: TCampaignSize;
    task: string;
  };
  tiktok: {
    hashtags: string[];
    accountTags: string[];
    numberOfMedias: number;
    keywordPlan: TTiktokKeywordPlan;
    tiktokSparkAds: boolean;
  };
  ugc: {
    isWithSubtitles: boolean;
    isSourceMaterialRequested: boolean;
    contentLanguage: TContentLanguage;
    updatedUgcContents: UGCContentSerializer[];
    ugcContents: UGCContentSerializer[];
    initialCashReward: number | null; // Based only on the selected UGC videos (excluding extra cash reward)
    extraCashReward: number | null;
  };
};

type TFormState = {
  accordionControls: Record<TCampaignAccordionKey, {isOpen: boolean}>;
  successes: string[];
  errors: string[];
  isEditable: boolean;
  hasTouchedPublish: boolean;
  isPublishTriggered: boolean;
  navbarControls: Record<number, {isOpen: boolean}>;
  tiktok: {
    isKeywordLimitOverReached: boolean;
  };
  warnings: string[];
};

export type TRecommendedPlans = {
  power_user?: TSubscriptionWithDiscountOptions;
  recommended?: TSubscriptionWithDiscountOptions;
};

// Define the specific type for the misc payload
type TMiscPayload = {
  number_of_feedposts: number;
  number_of_stories: number;
  number_of_reels: number;
  tiktok_number_of_medias: number;
  number_of_ugc: number;
  title: string;
  on_going: boolean;
  brand_name: string;
  long_description: string;
  manual_scheduling: boolean;
  brand_does: string;
  product_variant: string;
  selling_price: number;
  locations: string;
  campaign_locations?: TCampaignLocation[];
  casting_start: string;
  deadline: string;
  preparation: string;
  completed: string;
  preparation_days: number;
  commercial_days: number;
  cash_reward?: number; // Optional because it's conditional
  service_is_clothing: boolean;
  event?: boolean;
  event_attended_by_influencer?: boolean | null;
  event_address_place_id?: string | null;
  event_address_description?: string;
  event_address_street?: string | null;
  event_address_postal?: string | null;
  event_address_city?: string | null;
  event_address_country?: string | null;
  event_start_date?: string | null;
  event_end_date?: string | null;
  bonus_pool?: boolean;
  casting_days: number | null;
  service_is_physical_product: boolean;
  service_is_voucher: boolean;
  service_is_cash: boolean;
  digital_shop_url?: string | null;
  ugc_dont_description: string;
};

// Define specific payload types
type TInstagramPayload = {
  number_of_feedposts: number;
  number_of_stories: number;
  number_of_reels: number;
  hashtags: string;
  account_tags: string;
  task: string;
  link_sticker: string | null;
  instagram_branded_content_ads: boolean;
};

type TTiktokKeywordPlan = TTiktokKeywordSelection & {
  country?: RPNInput.Country;
  is_initialized?: boolean;
  website_url?: string;
};

type TTiktokPayload = {
  account_tags?: string;
  hashtags?: string;
  task: string;
  tiktok: true;
  tiktok_account_tags?: string;
  tiktok_hashtags?: string;
  tiktok_keyword_plan?: TTiktokKeywordPlan;
  tiktok_number_of_medias: number;
  tiktok_spark_ads: boolean;
};

type TUgcPayload = {
  ugc: true;
  subtitles: boolean;
  source_material: boolean;
  number_of_ugc: number;
  content_language: string;
  ugc_extra_cash_reward: number;
};

type TActions = {
  misc: {
    setAccordionControl: (key: TCampaignAccordionKey, isOpen: boolean) => void;
    setter: TSetter<TState>;
    setMiscValue<K extends keyof NonNullable<TComposedCampaign["misc"]>>(
      key: K,
      value: NonNullable<TComposedCampaign["misc"]>[K],
    ): void;
    setFormState<K extends keyof TFormState>(key: K, value: TFormState[K]): void;
    setNavbarControl: (key: number, isOpen: boolean) => void;
    changeCampaignDate(
      key: "castingStartDate" | "castingEndDate" | "preparationEndDate" | "endDate",
      value: string,
    ): void;
    changePhaseLength(key: "castingPhase" | "preparationPhase" | "activePhase", value: number): void;
    saveCampaign(isDraft?: boolean, campaignCreationType?: TCampaignCreationType): Promise<AxiosResponse<{id: number}>>;
    initWithExistingCampaign(campaign: CampaignOwnerDetailSerializer): void;
    initNewCampaign({
      campaignType,
      companyId,
      country,
      groupInvite,
      isAiCampaign,
      selectedKeywords,
    }: {
      campaignType: TCampaignCreationType;
      companyId?: number | null;
      country?: RPNInput.Country | TSupportedCampaignCountry;
      groupInvite?: TCreateCampaignFromGroup;
      isAiCampaign?: boolean;
      selectedKeywords?: string[];
    }): Promise<void>;
    markFieldSuccessIfValid(key: keyof (typeof SchemaCollection)["shape"]): void;
    validateAdmin(): boolean;
    validatePrice(): void;
    addMainCampaignImage(image: File | null): Promise<void>;
    addCampaignImages(images: File[]): Promise<void>;
    deleteImage(id: number): Promise<void>;
    resetStore(): void;
    buildPayload(): TMiscPayload;
    validateStepOne(): void;
    validateSteps(): boolean;
    validateStepThree(): void;
    validateStepTwo(): void;
    updateScheduling(): void;
    reorderProductImages(oldIndex: number, newIndex: number): void;
    addCampaignLocation(location?: TCampaignLocation): void;
    removeCampaignLocationAt(index: number): void;
    setCampaignLocationAt(index: number, location: TCampaignLocation): void;
    resetEventAddress(): void;
    carryOverAddressOnEventToggle(nextEvent: boolean): void;
    setCreativeSnippets(contentFormat: CreativeSnippetSerializer[], moodStyle: CreativeSnippetSerializer[]): void;
    setSnippetsLoading(loading: boolean): void;
    updateTriggerWordCount(wordCount: number): void;
    getBonusPoolLimit(): number | null;
    getWordCount(): number;
    getShouldRefetchSortedSnippets(): boolean;
  };
  tiktok: {
    setTiktokValue<K extends keyof NonNullable<TComposedCampaign["tiktok"]>>(
      key: K,
      value: NonNullable<TComposedCampaign["tiktok"]>[K],
    ): void;
    changeHashTag(newValue: string): void;
    buildKeywordPlan(): TTiktokKeywordPlan;
    buildPayload(): TTiktokPayload;
    changeAccountTag(newValue: string): void;
    clearKeywordPlan(): void;
    setKeywordLimitOverReached(isOverReached: boolean): void;
    setKeywordPlanCountry(country: RPNInput.Country): void;
    setKeywordPlanWebsiteUrl(websiteUrl: string): void;
    setKeywordSelection(selection: TTiktokKeywordSelection): void;
    validateStepTwo: () => void;
  };
  instagram: {
    buildPayload(): TInstagramPayload;
    changeHashTag(newValue: string): void;
    changeAccountTag(newValue: string): void;
    getNormalizedContentOptionOne(): TContentRequirements;
    getNormalizedContentOptionTwo(): TContentRequirements;
    getSelectedRequirementOption(): "first" | "second" | null;
    isInstagramWithReelOrPost(): boolean;
    setInstagramValue<K extends keyof NonNullable<TComposedCampaign["instagram"]>>(
      key: K,
      value: NonNullable<TComposedCampaign["instagram"]>[K],
    ): void;
    shouldShowPartnershipAds(): boolean;
    validateStepTwo: () => void;
  };
  socials: {
    markFieldSuccessIfValid(key: keyof (typeof SchemaCollection)["shape"], socialType: "instagram" | "tiktok"): void;
    setSocialsValue<K extends keyof NonNullable<TComposedCampaign["socials"]>>(
      key: K,
      value: NonNullable<TComposedCampaign["socials"]>[K],
    ): void;
    validateStepTwo(): void;
  };
  ugc: {
    initWithExistingUgc(campaign: CampaignOwnerDetailSerializer): Record<keyof TComposedCampaign["ugc"], any>;
    setUgcValue<K extends keyof NonNullable<TComposedCampaign["ugc"]>>(
      key: K,
      value: NonNullable<TComposedCampaign["ugc"]>[K],
    ): void;
    addUgcVideo(campaignId?: number, length?: 15 | 30): Promise<void>;
    deleteUgcVideo(index: number): Promise<void>;
    addUgcScene(index: number): void;
    deleteUgcScene(videoIndex: number, sceneIndex: number): void;
    changeUgcScene(videoIndex: number, sceneIndex: number, key: "content" | "name", value: string): void;
    changeUgcVideo<K extends keyof TComposedCampaign["ugc"]["updatedUgcContents"][number]>(
      index: number,
      key: K,
      value: TComposedCampaign["ugc"]["updatedUgcContents"][number][K],
    ): void;
    buildPayload(): TUgcPayload;
    saveUgcChanges(): Promise<void>;
    calculateInitialCashReward(): void;
  };
};

const initialState = {
  formState: {
    accordionControls: {} as Record<TCampaignAccordionKey, {isOpen: boolean}>,
    errors: [],
    isEditable: true,
    successes: [],
    hasTouchedPublish: false,
    isPublishTriggered: false,
    navbarControls: {} as Record<number, {isOpen: boolean}>,
    tiktok: {isKeywordLimitOverReached: false},
    warnings: [],
  },
  campaign: {
    misc: {
      applicationPhaseOver: false,
      campaignType: "instagram",
      isOngoing: false,
      brandName: "",
      isServiceClothing: false,
      longDescription: "",
      castingStartDate: "",
      castingEndDate: "",
      preparationEndDate: "",
      endDate: "",
      status: "draft",
      id: undefined,
      productVariant: "",
      usesStructuredProductVariants: true,
      title: "",
      locations: [],
      campaignLocations: [],
      picture: null,
      images: [],
      sellingPrice: null,
      brandDoes: "",
      cashReward: null,
      mainImageUploadProgress: null,
      manualScheduling: false,
      imageUploadProgress: null,
      eventDuration: "ONE_DAY",
      generativeAiAllowed: true,
      bonusPoolVersion: 2, // Version 2 is used for the newer performance-based bonus, while version 1 (speed-based bonus) is kept for compatibility reasons
      castingDays: null,
      preparationDays: null,
      activeDays: null,
      hasEditedCashReward: false,
      hasEditedPrice: false,
      hasChosenContentOption: true,
      isProduct: false,
      isVoucher: false,
      aiContentApprovalThreshold: DEFAULT_AI_CONTENT_APPROVAL_THRESHOLD,
      aiRecommendationsEnabled: true,
      isCash: false,
      contentApprovalCashFloorExempt: false,
      contentApprovalMode: "off",
      contentApprovalLocked: false,
      storyApprovalMode: "off",
      hasCommunityCodes: false,
      isExtended: false,
      isProInfluencer: false,
      ugcDontsDescription: "",
      sortedContentFormatSnippets: [],
      sortedMoodStyleSnippets: [],
      sortSnippetTriggerWordCount: 0,
      isLoadingSnippets: false,
    },
    instagram: {
      isLinkStickerEnabled: true,
      linkSticker: "",
      isLinkStickerTitleEnabled: false,
      linkStickerTitle: null,
      requiredContentUploads: null,
      hashtags: [],
      accountTags: [],
      contentOptionOne: null,
      contentOptionTwo: null,
      instagramPartnershipAds: false,
    },
    socials: {
      task: "",
    },
    tiktok: {
      hashtags: [],
      accountTags: [],
      numberOfMedias: 1,
      keywordPlan: {existing_keyword_ids: [], is_initialized: false, new_keywords: []},
      tiktokSparkAds: true,
    },
    ugc: {
      isWithSubtitles: false,
      isSourceMaterialRequested: false,
      contentLanguage: "Swiss German",
      updatedUgcContents: [],
      ugcContents: [],
      initialCashReward: null,
      extraCashReward: null,
    },
  },
} as Omit<TState, "actions">;

export const useCampaignFormStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialState,
        actions: {
          misc: {
            buildPayload: () => {
              const campaign = get().campaign.misc;
              const productVariant = campaign.usesStructuredProductVariants
                ? campaign.productVariant
                    .split("\n")
                    .map(line => line.trim())
                    .filter(Boolean)
                    .join("\n")
                : campaign.productVariant;
              return {
                //the following settings are only here to provide a non-null amount of content for each required media type
                //the campaignType-specific buildPayload methods will override the values if necessary
                number_of_feedposts: 0,
                number_of_stories: 0,
                number_of_reels: 0,
                tiktok_number_of_medias: 0,
                number_of_ugc: 0,
                title: campaign.title,
                on_going: campaign.isOngoing,
                brand_name: campaign.brandName,
                long_description: campaign.longDescription,
                manual_scheduling: campaign.manualScheduling,
                brand_does: campaign.brandDoes,
                private: campaign.privateForInfluencers,
                private_brand: campaign.privateForBrands,
                product_variant: campaign.isProduct ? productVariant : "",
                uses_structured_product_variants: campaign.usesStructuredProductVariants,
                selling_price: campaign.sellingPrice ?? 0,
                locations: campaign.locations.join(","),
                campaign_locations: campaign.campaignLocations.filter(location => location.place_id),
                casting_start: dayjs(campaign.castingStartDate).format(),
                deadline: dayjs(campaign.castingEndDate).format(),
                preparation: dayjs(campaign.preparationEndDate).format(),
                completed: dayjs(campaign.endDate).format(),
                preparation_days: dayjs(campaign.preparationEndDate).diff(dayjs(campaign.castingEndDate), "days"),
                commercial_days: dayjs(campaign.endDate).diff(dayjs(campaign.preparationEndDate), "days"),
                cash_reward: campaign.campaignType === "ugc" ? undefined : (campaign.cashReward ?? 0),
                service_is_clothing: campaign.isServiceClothing,
                event: campaign.event,
                event_attended_by_influencer: campaign.eventAttendedByInfluencers,
                event_address_place_id: campaign.eventAddressPlaceId,
                event_address_description: campaign.eventAddressDescription,
                event_address_street: campaign.eventAddressStreet,
                event_address_postal: campaign.eventAddressPostal,
                event_address_city: campaign.eventAddressCity,
                event_address_country: campaign.eventAddressCountry,
                event_start_date: campaign.eventStartDate,
                event_end_date: campaign.eventEndDate,
                bonus_pool: campaign.isBonusPoolActive,
                bonus_pool_version: campaign.bonusPoolVersion,
                casting_days: campaign.castingDays,
                service_is_physical_product: campaign.isProduct,
                service_is_voucher: campaign.isVoucher,
                has_community_codes: campaign.hasCommunityCodes,
                service_is_cash: campaign.isCash,
                digital_shop_url: campaign.digitalShopUrl,
                ai_recommendations_enabled: campaign.aiRecommendationsEnabled,
                ai_content_approval_threshold: campaign.aiContentApprovalThreshold,
                content_approval_mode: campaign.contentApprovalMode,
                story_approval_mode: campaign.storyApprovalMode,
                pro_influencer: campaign.isProInfluencer,
                ugc_dont_description: get().campaign.misc.ugcDontsDescription,
                sorted_content_format_snippets: campaign.sortedContentFormatSnippets,
                sorted_mood_style_snippets: campaign.sortedMoodStyleSnippets,
                sort_snippet_trigger_word_count: campaign.sortSnippetTriggerWordCount,
              };
            },
            addMainCampaignImage: async image => {
              try {
                const campaign = get().campaign;
                const response = await client.patch(
                  CAMPAIGN_KEYS.mCampaign(campaign.misc.id!)[0],
                  {
                    picture: image,
                    picture1200: image,
                    picture640: image,
                    picture_vertial1080: image,
                    picture_vertial576: image,
                  },
                  {
                    headers: {
                      "Content-Type": "multipart/form-data",
                    },
                    onUploadProgress: progressEvent => {
                      if (!progressEvent.total) return;

                      const progress = (progressEvent.loaded / progressEvent.total) * 100;
                      set(state => {
                        state.campaign.misc.mainImageUploadProgress = Math.floor(progress);
                      });
                    },
                  },
                );

                set(state => {
                  state.campaign.misc.picture = response.data.picture;
                  state.campaign.misc.mainImageUploadProgress = null;

                  state.formState.warnings = state.formState.warnings.filter(fieldKey => fieldKey !== "picture");
                  state.formState.successes = state.formState.successes.filter(fieldKey => fieldKey !== "picture");
                });

                get().actions.misc.markFieldSuccessIfValid("picture");
              } catch (error) {
                const err = (error as TError).response?.data?.picture?.[0];
                toast.error(`${i18n.t("error")} ${!!err ? " - " + err : ""}`);
                setTimeout(() => {
                  set(state => {
                    state.campaign.misc.mainImageUploadProgress = null;
                  });
                }, 1500);
              }
            },
            addCampaignImages: async images => {
              const uploadPromises: Promise<AxiosResponse<TFileUploadResponse>>[] = [];
              const currentImages = get().campaign.misc.images;

              for (let i = 0; i < images.length; i++) {
                if (5 - currentImages.length <= i) break;

                const image = images[i];
                const uploadPromise = client.post<TFileUploadResponse>(
                  `/campaign/${get().campaign.misc.id}/upload_image/`,
                  {image: image, thumbnail: image},
                  {
                    headers: {
                      "Content-Type": "multipart/form-data",
                    },
                    onUploadProgress: progressEvent => {
                      if (!progressEvent.total) return;

                      const progress = (progressEvent.loaded / progressEvent.total) * 100;
                      set(state => {
                        state.campaign.misc.imageUploadProgress = Math.floor(progress);
                      });
                    },
                  },
                );
                uploadPromises.push(uploadPromise);
              }

              try {
                const responses = await Promise.all(uploadPromises);

                set(state => {
                  state.campaign.misc.images = [...state.campaign.misc.images, ...responses.map(resp => resp.data)];
                  state.campaign.misc.imageUploadProgress = null;
                });
              } catch (error) {
                const err = (error as TError).response?.data?.image?.[0];
                toast.error(`${i18n.t("error")} ${!!err ? " - " + err : ""}`);
                setTimeout(() => {
                  set(state => {
                    state.campaign.misc.imageUploadProgress = null;
                  });
                }, 1500);
              }
            },
            deleteImage: async id => {
              const campaign = get().campaign;
              const pictureInUploadedImages = campaign.misc.images.find(image => image.id === id);

              if (!pictureInUploadedImages) return;

              await client.post(CAMPAIGN_KEYS.mCampaignImageDeletion(campaign.misc.id!)[0], {
                id: pictureInUploadedImages.id,
              });
              set(
                state => {
                  state.campaign.misc.images = state.campaign.misc.images.filter(image => image.id !== id);
                },
                false,
                "campaignFormStore/deleteImage",
              );
            },
            setAccordionControl: (key, isOpen) =>
              set(state => {
                state.formState.accordionControls[key] = {isOpen};
              }),
            setter: fn => set(fn, false, "campaignFormStore/setter"),
            setMiscValue: (key, value) =>
              set(
                state => {
                  if (key === "cashReward" && typeof value === "number") {
                    const oldCashReward = get().campaign.misc.cashReward ?? 0;
                    const newCashReward = value!;

                    if (oldCashReward >= BONUS_POOL_MIN_CASH_REWARD && newCashReward < BONUS_POOL_MIN_CASH_REWARD) {
                      state.campaign.misc.isBonusPoolActive = false;
                    }
                  }

                  state.formState.warnings = state.formState.warnings.filter(fieldKey => fieldKey !== key);
                  state.formState.successes = state.formState.successes.filter(fieldKey => fieldKey !== key);

                  if (["castingDays", "preparationDays", "activeDays"].includes(key as string)) {
                    state.formState.warnings = state.formState.warnings.filter(
                      fieldKey => fieldKey !== "campaignDates",
                    );
                    state.formState.successes = state.formState.successes.filter(
                      fieldKey => fieldKey !== "campaignDates",
                    );
                  }

                  state.campaign.misc[key] = value;
                },
                false,
                "campaignFormStore/setMiscValue",
              ),
            setFormState: (key, value) =>
              set(
                state => {
                  state.formState[key] = value;
                },
                false,
                "campaignFormStore/setFormState",
              ),
            setNavbarControl: (key, isOpen) =>
              set(state => {
                state.formState.navbarControls[key] = {isOpen};
              }),
            changeCampaignDate: (key, value) => {
              return set(
                state => {
                  const previousValue = dayjs(state.campaign.misc[key]);
                  const diff = dayjs(value).diff(previousValue);
                  if (key === "castingStartDate") {
                    state.campaign.misc.castingStartDate = value;

                    if (state.campaign.misc.event) return;

                    state.campaign.misc.castingEndDate = dayjs(state.campaign.misc.castingEndDate)
                      .add(diff)
                      .toISOString();
                    state.campaign.misc.preparationEndDate = dayjs(state.campaign.misc.preparationEndDate)
                      .add(diff)
                      .toISOString();
                    state.campaign.misc.endDate = dayjs(state.campaign.misc.endDate).add(diff).toISOString();
                  } else if (key === "castingEndDate") {
                    state.campaign.misc.castingEndDate = value;
                    state.campaign.misc.preparationEndDate = dayjs(state.campaign.misc.preparationEndDate)
                      .add(diff)
                      .toISOString();
                    state.campaign.misc.endDate = dayjs(state.campaign.misc.endDate).add(diff).toISOString();
                  } else if (key === "preparationEndDate") {
                    state.campaign.misc.preparationEndDate = value;
                    state.campaign.misc.endDate = dayjs(state.campaign.misc.endDate).add(diff).toISOString();
                  } else if (key === "endDate") {
                    state.campaign.misc.endDate = value;
                  }
                  state.campaign.misc[key] = value;
                },
                false,
                "campaignFormStore/changeCampaignDate",
              );
            },
            changePhaseLength: (key, value) => {
              const campaign = get().campaign.misc;
              const castingStartDate = dayjs(campaign.castingStartDate);
              const castingEndDate = dayjs(campaign.castingEndDate);
              const preparationEndDate = dayjs(campaign.preparationEndDate);
              if (key === "castingPhase") {
                if (campaign.event)
                  get().actions.misc.changeCampaignDate(
                    "castingStartDate",
                    dayjs(campaign.eventStartDate).subtract(value, "days").toISOString(),
                  );
                else
                  get().actions.misc.changeCampaignDate(
                    "castingEndDate",
                    castingStartDate.add(value, "days").toISOString(),
                  );
              } else if (key === "preparationPhase") {
                get().actions.misc.changeCampaignDate(
                  "preparationEndDate",
                  castingEndDate.add(value, "days").toISOString(),
                );
              } else if (key === "activePhase") {
                get().actions.misc.changeCampaignDate("endDate", preparationEndDate.add(value, "days").toISOString());
              }
            },
            initNewCampaign: async ({
              campaignType,
              companyId,
              country,
              groupInvite,
              isAiCampaign = false,
              selectedKeywords,
            }) => {
              // Reset store to ensure a fresh campaign is created (POST not PATCH).
              // Without this, a stale campaign.misc.id from a previous creation
              // causes saveCampaign to PATCH the old campaign instead of creating a new one.
              get().actions.misc.resetStore();

              const user = useUserStore.getState().user;
              const resolvedCompanyId = companyId ?? getDefaultStore().get(createCampaignForCompanyAtom);
              const selectedCompany = useSelectedCompany();
              let company = selectedCompany;

              if (resolvedCompanyId) {
                company = (await client.get(`/company/${resolvedCompanyId}/`)).data;
              }

              set(
                state => {
                  state.campaign.misc.campaignType = campaignType;
                  state.campaign.misc.companyId = company?.id;
                  state.campaign.misc.brandName = company?.company_name || "";

                  const initialCampaignLocation =
                    country && SUPPORTED_CAMPAIGN_COUNTRIES.includes(country as TSupportedCampaignCountry)
                      ? COUNTRY_CODE_TO_CAMPAIGN_LOCATION[country as TSupportedCampaignCountry]
                      : null;

                  state.campaign.misc.locations = initialCampaignLocation
                    ? [initialCampaignLocation]
                    : COUNTRY_FILTERS.includes(company?.country as TCountry)
                      ? [company?.country as TCountry]
                      : ["Switzerland"];
                  if (!isAiCampaign) {
                    state.campaign.misc.isProduct = true;
                  }
                  state.campaign.misc.castingStartDate = dayjs().toISOString();
                  state.campaign.misc.castingEndDate = dayjs().add(CASTING_DAYS, "days").toISOString();
                  state.campaign.misc.preparationEndDate = dayjs()
                    .add(CASTING_DAYS + PREPARATION_DAYS, "days")
                    .toISOString();
                  state.campaign.misc.endDate = dayjs()
                    .add(
                      CASTING_DAYS + PREPARATION_DAYS + (campaignType === "ugc" ? ACTIVE_DAYS_UGC : ACTIVE_DAYS),
                      "days",
                    )
                    .toISOString();

                  if (campaignType === "instagram" || campaignType === "instagram_and_tiktok") {
                    state.campaign.instagram.instagramPartnershipAds = !!company?.has_enabled_instagram_partnership_ads;
                    if (company?.username) state.campaign.instagram.accountTags = [company.username];
                  }

                  if (campaignType === "tiktok" || campaignType === "instagram_and_tiktok") {
                    state.campaign.tiktok.tiktokSparkAds = !!company?.has_enabled_tiktok_spark_ads;
                    if (company?.tiktok_username) state.campaign.tiktok.accountTags = [company.tiktok_username];
                  }

                  const lastCampaignSize = company?.last_campaign_size as TCampaignSize;

                  if (
                    campaignType !== "ugc" &&
                    lastCampaignSize &&
                    getCampaignSizeOptions({
                      hasSubscription: !!user.package_subscription,
                      planName: user.package_subscription?.name,
                    }).includes(lastCampaignSize)
                  ) {
                    state.campaign.socials.campaignSize = lastCampaignSize;
                  }

                  if (
                    (campaignType === "tiktok" || campaignType === "instagram_and_tiktok") &&
                    selectedKeywords?.length
                  ) {
                    state.campaign.tiktok.keywordPlan.existing_keyword_ids = selectedKeywords;
                    state.campaign.tiktok.keywordPlan.is_initialized = true;

                    if (country) {
                      state.campaign.tiktok.keywordPlan.country = country;
                    }
                  }

                  if (campaignType !== "ugc") {
                    state.campaign.misc.isProInfluencer = !!user.package_subscription?.pro_creator_mode;
                  }
                },
                false,
                "campaignFormStore/initNewCampaign",
              );
              const response = await get().actions.misc.saveCampaign(true, campaignType);
              if (campaignType === "ugc") await get().actions.ugc.addUgcVideo(response.data.id);
              set(state => {
                state.campaign.misc.id = response.data.id;
              });

              if (groupInvite) {
                try {
                  await client.post(GROUP_KEYS.mGroupCampaignInvite(groupInvite.groupId)[0], {
                    campaign_id: response.data.id,
                    ...(groupInvite.filterParams && {filters: groupInvite.filterParams}),
                  });
                } catch (error) {
                  handleErrorWithToast({error: error as TError});
                }
                getDefaultStore().set(createCampaignFromGroupAtom, null);
              }

              const campaignTypes =
                campaignType === "instagram_and_tiktok"
                  ? ["instagram", "tiktok"]
                  : [campaignType === "tiktok" ? "tiktok" : campaignType === "ugc" ? "ugc" : "instagram"];

              campaignTypes.forEach(campaignType =>
                capturePostHogEvent(
                  isAiCampaign ? PostHogEvents.StartCampaignCreationAI : PostHogEvents.StartCampaignCreation,
                  {
                    campaignId: Number(response.data.id),
                    campaignType,
                    companyId: resolvedCompanyId ? Number(resolvedCompanyId) : undefined,
                  },
                ),
              );

              if (!isAiCampaign) {
                window.location.href = `/campaign/${response.data.id}/edit/?isUgc=${campaignType === "ugc"}&step=1`;
              }
            },
            initWithExistingCampaign: campaign => {
              const combinedCampaign = campaign.instagram && campaign.tiktok;

              // TODO: remove this type casing once DjangoTypes is regenerated
              const existingPlan = (campaign as {tiktok_keyword_plan?: TTiktokKeywordPlanRead}).tiktok_keyword_plan;
              const keywordPlan: TTiktokKeywordPlan = {
                existing_keyword_ids: existingPlan?.existing_keyword_ids ?? [],
                is_initialized: Boolean(existingPlan?.is_initialized),
                new_keywords: existingPlan?.new_keywords ?? [],
                ...(existingPlan?.country ? {country: existingPlan.country} : {}),
                ...(existingPlan?.website_url ? {website_url: existingPlan.website_url} : {}),
              };

              set(
                state => {
                  state.formState.isEditable =
                    campaign.status === "draft" ||
                    campaign.status === "planned" ||
                    useUserStore.getState().user.user_type === "admin";
                  state.campaign = {
                    misc: {
                      applicationPhaseOver: campaign.application_phase_over ?? false,
                      brandName: campaign.company_profile?.company_name || campaign.brand_name || "",
                      isOngoing: Boolean(campaign.on_going),
                      title: campaign.title || "",
                      longDescription: campaign.long_description || "",
                      brandDoes: campaign.brand_does || "",
                      productVariant: campaign.product_variant || "",
                      usesStructuredProductVariants: Boolean(campaign.uses_structured_product_variants),
                      sellingPrice: campaign.selling_price || 0,
                      isServiceClothing: Boolean(campaign.service_is_clothing),
                      picture: campaign.picture || null,
                      cashReward: campaign.cash_reward || 0,
                      images: campaign.images?.map(image => ({
                        image: image.image!,
                        id: image.id,
                      }))!,
                      status: campaign.status || "draft",

                      campaignType:
                        campaign.instagram && campaign.tiktok
                          ? "instagram_and_tiktok"
                          : campaign.ugc
                            ? "ugc"
                            : campaign.tiktok
                              ? "tiktok"
                              : "instagram",
                      id: campaign.id,
                      castingStartDate: campaign.casting_start || "",
                      castingEndDate: campaign.deadline || "",
                      preparationEndDate: campaign.preparation || "",
                      privateForBrands: campaign.private_brand ?? false,
                      privateForInfluencers: campaign.private ?? false,
                      endDate: campaign.completed || "",
                      locations:
                        campaign.locations?.map((loc: string) =>
                          loc === "Schweiz" ? "Switzerland" : loc === "Deutschland" ? "Germany" : (loc as TCountry),
                        ) ?? [],
                      castingDays: campaign.casting_days!,
                      preparationDays: campaign.preparation_days!,
                      activeDays: campaign.commercial_days!,
                      event: campaign.event,
                      eventAttendedByInfluencers: campaign.event_attended_by_influencer,
                      eventAddressPlaceId: campaign.event_address_place_id,
                      eventAddressDescription: campaign.event_address_description ?? "",
                      eventAddressStreet: campaign.event_address_street,
                      eventAddressPostal: campaign.event_address_postal,
                      eventAddressCity: campaign.event_address_city,
                      eventAddressCountry: campaign.event_address_country,
                      campaignLocations: campaign.campaign_locations ?? [],
                      eventStartDate: campaign.event_start_date ?? dayjs().add(21, "days").toISOString(),
                      eventEndDate: campaign.event_end_date
                        ? campaign.event_end_date
                        : campaign.event_start_date
                          ? dayjs(campaign.event_start_date).add(22, "days").toISOString()
                          : dayjs().add(21, "days").toISOString(),
                      eventDuration:
                        campaign.event_start_date &&
                        campaign.event_end_date &&
                        !dayjs(campaign.event_start_date).isSame(campaign.event_end_date, "day")
                          ? "MULTIPLE_DAYS"
                          : "ONE_DAY",
                      isBonusPoolActive: campaign.bonus_pool,
                      bonusPoolVersion: campaign.bonus_pool_version ?? 1,
                      generativeAiAllowed: campaign.generative_ai_allowed ?? true,
                      companyId: campaign.company_profile?.id,
                      companyImage: campaign.company_profile?.picture320 ?? campaign.company_profile?.picture,
                      canCreateEvents: campaign.company_profile?.event_creator,
                      skipCharacterLimits: campaign.company_profile?.skip_character_limits,
                      hasChosenContentOption: true,
                      isProduct: campaign.service_is_physical_product ?? false,
                      isVoucher: campaign.service_is_voucher ?? false,
                      hasCommunityCodes: campaign.has_community_codes ?? false,
                      isCash: campaign.service_is_cash ?? false,
                      aiContentApprovalThreshold:
                        campaign.ai_content_approval_threshold ?? DEFAULT_AI_CONTENT_APPROVAL_THRESHOLD,
                      aiRecommendationsEnabled: campaign.ai_recommendations_enabled ?? true,
                      contentApprovalCashFloorExempt: campaign.content_approval_cash_floor_exempt ?? false,
                      contentApprovalMode: asContentApprovalMode(campaign.content_approval_mode),
                      contentApprovalLocked: campaign.content_approval_locked ?? false,
                      storyApprovalMode: asStoryApprovalMode(campaign.story_approval_mode),
                      isExtended: campaign.is_completed_date_extended ?? false,
                      isProInfluencer: campaign.pro_influencer!,
                      digitalShopUrl: campaign.digital_shop_url,
                      ugcDontsDescription: campaign.ugc_dont_description || "",
                      sortedContentFormatSnippets: campaign.sorted_content_format_snippets || [],
                      sortedMoodStyleSnippets: campaign.sorted_mood_style_snippets || [],
                      sortSnippetTriggerWordCount: campaign.sort_snippet_trigger_word_count || 0,
                      isLoadingSnippets: false,
                      manualScheduling: campaign.manual_scheduling ?? false,
                    },
                    instagram: campaign.instagram
                      ? {
                          isLinkStickerEnabled: campaign.link_sticker !== null,
                          linkSticker: campaign.link_sticker ?? null,
                          isLinkStickerTitleEnabled: !!campaign.link_sticker_title,
                          linkStickerTitle: campaign.link_sticker_title,
                          requiredContentUploads: {
                            stories: campaign.number_of_stories || 0,
                            posts: campaign.number_of_feedposts || 0,
                            reels: campaign.number_of_reels || 0,
                          },
                          hashtags: (campaign.hashtags ?? "").split(":"),
                          accountTags: (campaign.account_tags ?? "").split(":"),
                          instagramPartnershipAds: !!campaign.instagram_branded_content_ads,
                        }
                      : initialState.campaign.instagram,
                    tiktok: campaign.tiktok
                      ? {
                          numberOfMedias: campaign.tiktok_number_of_medias || 0,
                          hashtags: ((combinedCampaign ? campaign.tiktok_hashtags : campaign.hashtags) ?? "").split(
                            ":",
                          ),
                          accountTags: (
                            (combinedCampaign ? campaign.tiktok_account_tags : campaign.account_tags) ?? ""
                          ).split(":"),
                          keywordPlan,
                          tiktokSparkAds: campaign.tiktok_spark_ads!,
                        }
                      : initialState.campaign.tiktok,
                    socials: campaign.ugc
                      ? initialState.campaign.socials
                      : {
                          campaignSize:
                            campaign.campaign_size && campaign.campaign_size in CAMPAIGN_SIZE_RANGES
                              ? (campaign.campaign_size as TCampaignSize)
                              : undefined,
                          task: campaign.task || "",
                        },
                    ugc: campaign.ugc ? get().actions.ugc.initWithExistingUgc(campaign) : initialState.campaign.ugc,
                  };
                },
                false,
                "campaignFormStore/initWithExistingCampaign",
              );
              useCampaignManagementStore.getState().setValue("campaign", campaign);

              if (campaign.ugc) get().actions.ugc.calculateInitialCashReward();

              // Update the scheduling for existing draft events
              if (
                !campaign.manual_scheduling &&
                campaign.draft &&
                campaign.event &&
                campaign.preparation_days !== EVENT_PREPARATION_DAYS
              )
                get().actions.misc.updateScheduling();

              // Re-adjust the dates for those draft campaigns whose casting start date is in the past
              if (
                !campaign.manual_scheduling &&
                campaign.draft &&
                dayjs(campaign.casting_start).isBefore(dayjs(), "day")
              )
                get().actions.misc.updateScheduling();

              const selectedCompany = useSelectedCompany();
              const instagramAccountTags = get().campaign.instagram.accountTags;
              const tiktokAccountTags = get().campaign.tiktok.accountTags;

              if (
                campaign.instagram &&
                selectedCompany?.username &&
                (!instagramAccountTags.length ||
                  (instagramAccountTags.length === 1 &&
                    ["", selectedCompany.tiktok_username].includes(instagramAccountTags[0])))
              )
                set(state => {
                  state.campaign.instagram.accountTags = [selectedCompany.username!];
                });

              if (
                campaign.tiktok &&
                selectedCompany?.tiktok_username &&
                (!tiktokAccountTags.length ||
                  (tiktokAccountTags.length === 1 && ["", selectedCompany.username].includes(tiktokAccountTags[0])))
              )
                set(state => {
                  state.campaign.tiktok.accountTags = [selectedCompany.tiktok_username!];
                });

              if (
                campaign.ugc ||
                (campaign.cash_reward ?? 0) < BONUS_POOL_MIN_CASH_REWARD ||
                (campaign.instagram && campaign.number_of_feedposts === 0 && campaign.number_of_reels === 0)
              ) {
                set(state => {
                  state.campaign.misc.isBonusPoolActive = false;
                });
              }

              const campaignTypes =
                campaign.tiktok && campaign.instagram
                  ? ["instagram", "tiktok"]
                  : [campaign.tiktok ? "tiktok" : campaign.ugc ? "ugc" : "instagram"];

              campaignTypes.forEach(campaignType =>
                capturePostHogEvent(PostHogEvents.ContinueDraftCampaignCreation, {
                  campaignId: Number(campaign.id),
                  campaignType,
                  companyId: campaign.company_profile?.id ? Number(campaign.company_profile?.id) : undefined,
                }),
              );
            },
            saveCampaign: async (isDraft, campaignCreationType) => {
              set(
                state => {
                  if (!!get().campaign.instagram.linkSticker) {
                    state.campaign.instagram.linkSticker = urlify(get().campaign.instagram.linkSticker);
                  }
                },
                false,
                "campaignFormStore/urlifyLinkSticker",
              );

              if (!!get().campaign.misc.digitalShopUrl) {
                set(
                  state => {
                    state.campaign.misc.digitalShopUrl = urlify(get().campaign.misc.digitalShopUrl);
                  },
                  false,
                  "campaignFormStore/urlifyDigitalShopUrl",
                );
              }

              const campaign = get().campaign.misc;
              const isAgency = useIsAgency();
              const agencyId = isAgency ? useUserStore.getState().user.agencies?.at(0)?.agency?.id : undefined;
              const campaignType = campaignCreationType ?? campaign.campaignType;

              const payload = {
                ...get().actions.misc.buildPayload(),
                ...(campaignType === "instagram_and_tiktok"
                  ? {
                      ...get().actions.instagram.buildPayload(),
                      ...get().actions.tiktok.buildPayload(),
                    }
                  : get().actions[campaignType].buildPayload()),
                ...(isDraft !== undefined && {draft: isDraft}),
              };
              if (!campaign.id) {
                const response = await client.post(CAMPAIGN_KEYS.mCampaignList[0], {
                  ...payload,
                  company_profile: get().campaign.misc.companyId,
                });

                if (campaignType === "ugc") {
                  const implicitlyGeneratedUgcVideoId = response.data.ugc_details[0].id as number;
                  await Promise.all([
                    get().actions.ugc.saveUgcChanges(),
                    client.post(CAMPAIGN_KEYS.mCampaignUgcContentRemoval[0], {
                      ugc_content_id: implicitlyGeneratedUgcVideoId,
                    }),
                  ]);
                }

                return response;
              } else {
                if (campaignType === "ugc") {
                  await get().actions.ugc.saveUgcChanges();
                }

                const response = await client.patch(CAMPAIGN_KEYS.mCampaign(campaign.id!)[0], payload);
                [
                  CAMPAIGN_KEYS.qCampaignInvitedGroups(campaign.id),
                  GROUP_KEYS.qGroupList(useGroupListQueryParams({agencyId, companyId: campaign.companyId})),
                ].map(queryKey => queryClient.refetchQueries({queryKey}));

                return response;
              }
            },
            resetStore: () => {
              set(
                state => {
                  state.campaign = initialState.campaign;
                  state.formState = initialState.formState;
                },
                false,
                "campaignFormStore/resetStore",
              );
            },
            markFieldSuccessIfValid: key => {
              const campaign = get().campaign;
              const campaignType = campaign.misc.campaignType;

              const schema = SchemaCollection.pick({[key as "title"]: true});

              const campaignData = {...campaign.misc, ...campaign.socials};
              const result = schema.safeParse(campaignData);

              if (result.success) {
                set(state => {
                  state.formState.successes = [...state.formState.successes, key];
                });
              }

              if (campaignType === "instagram_and_tiktok") {
                get().actions.socials.markFieldSuccessIfValid(key, "instagram");
                get().actions.socials.markFieldSuccessIfValid(key, "tiktok");
              }

              if (campaignType === "instagram" || campaignType === "tiktok") {
                get().actions.socials.markFieldSuccessIfValid(key, campaignType);
              }
            },
            validateAdmin: () => {
              const campaignType = get().campaign.misc.campaignType;
              if (campaignType === "instagram" || campaignType === "instagram_and_tiktok") {
                const result = CampaignInstagramSchema.safeParse(get().campaign.instagram);

                const warningFields = !result.success
                  ? result.error.issues.map(issue => `instagram-${issue.path[0].toString()}`)
                  : [];

                set(state => {
                  state.formState.warnings = [...state.formState.warnings, ...warningFields];
                  state.formState.successes = [
                    ...state.formState.successes,
                    ...Object.keys(CampaignInstagramSchema.shape)
                      .map(fieldKey => `instagram-${fieldKey}`)
                      .filter(key => !warningFields.includes(`instagram-${key}`)),
                  ];
                });

                return result.success;
              }

              return true;
            },
            validatePrice: () => {
              const campaign = get().campaign.misc;
              const cashRewardFieldKey = campaign.campaignType === "ugc" ? "extraCashReward" : "cashReward";

              set(state => {
                const isValid = campaign.isCash
                  ? campaign.campaignType === "ugc"
                    ? (get().campaign.ugc.extraCashReward ?? 0) > 0
                    : (campaign.cashReward ?? 0) > 0
                  : (campaign.sellingPrice ?? 0) > 0 ||
                    (campaign.campaignType === "ugc"
                      ? (get().campaign.ugc.extraCashReward ?? 0) > 0
                      : (campaign.cashReward ?? 0) > 0);

                if (isValid) {
                  state.formState.warnings = state.formState.warnings.filter(
                    warning => warning !== "cashReward" && warning !== "extraCashReward" && warning !== "sellingPrice",
                  );

                  if (campaign.cashReward || !!get().campaign.ugc.extraCashReward) {
                    state.formState.successes = [...state.formState.successes, cashRewardFieldKey];
                  }

                  if (campaign.sellingPrice) {
                    state.formState.successes = [...state.formState.successes, "sellingPrice"];
                  }
                } else {
                  state.formState.warnings = [...state.formState.warnings, cashRewardFieldKey, "sellingPrice"];
                }
              });
            },
            validateStepOne: () => {
              const campaign = get().campaign.misc;

              let baseSchema = get().campaign.misc.skipCharacterLimits
                ? CampaignMiscStepOneSchema.omit({
                    longDescription: true,
                    brandDoes: true,
                  })
                : CampaignMiscStepOneSchema;

              if (get().campaign.misc.isVoucher) {
                baseSchema = baseSchema.extend(DigitalShopUrlSchema.shape);
              }

              const miscResult = baseSchema.safeParse(campaign);
              get().actions.misc.validatePrice();

              set(
                state => {
                  const warningFields = !miscResult.success
                    ? miscResult.error.issues.map(issue => issue.path[0].toString())
                    : [];

                  state.formState.warnings = [...state.formState.warnings, ...warningFields];
                  state.formState.successes = [
                    ...state.formState.successes,
                    ...Object.keys(baseSchema.shape).filter(key => !warningFields.includes(key)),
                  ];
                },
                false,
                "campaignFormStore/misc/validateStepOne",
              );
            },
            validateSteps: () => {
              const user = useUserStore.getState().user;
              const isAdminValidated = user.user_type === "admin" && get().actions.misc.validateAdmin();

              set(
                state => {
                  state.formState.errors = [];
                  state.formState.warnings = [];
                  state.formState.successes = [];
                },
                false,
                "campaignFormStore/misc/validateSteps",
              );

              get().actions.misc.validateStepOne();
              get().actions.misc.validateStepTwo();
              get().actions.misc.validateStepThree();

              if (
                user.package_subscription &&
                !user.package_subscription.pro_creator_mode &&
                get().campaign.misc.isProInfluencer
              ) {
                set(state => {
                  state.formState.warnings = [...state.formState.warnings, "pro_creator_mode"];
                  state.formState.errors = [...state.formState.errors, "error_missing_pro_mode_permission"];
                });
              }

              set(
                state => {
                  state.formState.hasTouchedPublish = true;
                  state.formState.isPublishTriggered = true;
                },
                false,
                "campaignFormStore/misc/validateSteps",
              );

              return isAdminValidated || isEmpty(get().formState.warnings);
            },
            validateStepThree: () => {
              const campaign = get().campaign.misc;
              const isShortTerm = useUserStore.getState().user.package_subscription?.short_term_campaigns ?? false;

              const minCastingDays = isShortTerm ? MIN_CASTING_DAYS_SHORT_TERM : MIN_CASTING_DAYS;
              const minPreparationDays = campaign.isCash
                ? MIN_PREPARATION_DAYS_CASH
                : isShortTerm
                  ? MIN_PREPARATION_DAYS_SHORT_TERM
                  : MIN_PREPARATION_DAYS;

              let schema: z.ZodObject<Record<string, z.ZodTypeAny>> = CampaignMiscStepThreeSchema.extend({
                activeDays: z
                  .number()
                  .min(isShortTerm ? MIN_ACTIVE_DAYS_SHORT_TERM : MIN_ACTIVE_DAYS)
                  .max(MAX_ACTIVE_DAYS),
                castingDays: z.number().min(minCastingDays).max(MAX_CASTING_DAYS),
                preparationDays: z.number().min(minPreparationDays).max(MAX_PREPARATION_DAYS),
              });

              if (campaign.event) {
                schema = schema.omit({activeDays: true, castingDays: true, preparationDays: true});
              } else if (campaign.isOngoing) {
                schema = schema.omit({castingDays: true});
              }

              if (!campaign.event) {
                schema = schema.omit({eventAddressDescription: true});
              }

              const result = schema.safeParse(campaign);
              const warningFields = !result.success ? result.error.issues.map(issue => issue.path[0].toString()) : [];

              const dateFields = ["castingDays", "preparationDays", "activeDays"];
              const hasDateError = warningFields.some(field => dateFields.includes(field));

              set(
                state => {
                  state.formState.warnings = [
                    ...state.formState.warnings,
                    ...warningFields,
                    ...(hasDateError ? ["campaignDates"] : []),
                  ];
                  state.formState.successes = [
                    ...state.formState.successes,
                    ...Object.keys(schema.shape).filter(key => !warningFields.includes(key)),
                    ...(!hasDateError ? ["campaignDates"] : []),
                  ];
                },
                false,
                "campaignFormStore/misc/validateStepThree",
              );
            },
            validateStepTwo: () => {
              const campaignType = get().campaign.misc.campaignType;

              if (campaignType === "instagram") {
                get().actions.instagram.validateStepTwo();
              }

              if (campaignType === "tiktok") {
                get().actions.tiktok.validateStepTwo();
              }

              if (campaignType === "instagram_and_tiktok") {
                get().actions.instagram.validateStepTwo();
                get().actions.tiktok.validateStepTwo();
              }

              if (get().formState.warnings.includes("pro_creator_mode") && !get().campaign.misc.isProInfluencer) {
                set(state => {
                  state.formState.warnings = state.formState.warnings.filter(key => key !== "pro_creator_mode");
                  state.formState.errors = state.formState.errors.filter(
                    key => key !== "error_missing_pro_mode_permission",
                  );
                });
              }
            },
            updateScheduling: () => {
              const campaign = get().campaign.misc;
              const setCampaignValue = get().actions.misc.setMiscValue;
              const isShortTerm = useUserStore.getState().user.package_subscription?.short_term_campaigns ?? false;
              const minCastingDays = isShortTerm ? MIN_CASTING_DAYS_SHORT_TERM : MIN_CASTING_DAYS;
              const minPreparationDays = campaign.isCash
                ? MIN_PREPARATION_DAYS_CASH
                : isShortTerm
                  ? MIN_PREPARATION_DAYS_SHORT_TERM
                  : MIN_PREPARATION_DAYS;
              const minActiveDays = isShortTerm ? MIN_ACTIVE_DAYS_SHORT_TERM : MIN_ACTIVE_DAYS;
              const castingDays = Math.max(campaign.castingDays ?? CASTING_DAYS, minCastingDays);
              const preparationDays = Math.max(campaign.preparationDays ?? PREPARATION_DAYS, minPreparationDays);
              const activeDays = Math.max(
                campaign.activeDays ?? (campaign.campaignType === "ugc" ? ACTIVE_DAYS_UGC : ACTIVE_DAYS),
                minActiveDays,
              );

              setCampaignValue("castingStartDate", dayjs().toISOString());
              setCampaignValue(
                "castingEndDate",
                campaign.event
                  ? dayjs(campaign.eventStartDate).subtract(EVENT_PREPARATION_DAYS, "days").toISOString()
                  : dayjs().add(castingDays, "days").toISOString(),
              );
              setCampaignValue(
                "preparationEndDate",
                campaign.event
                  ? campaign.eventStartDate!
                  : dayjs()
                      .add(castingDays + preparationDays, "days")
                      .toISOString(),
              );
              setCampaignValue(
                "endDate",
                campaign.event
                  ? dayjs(campaign.eventEndDate).add(EVENT_ACTIVE_DAYS, "days").toISOString()
                  : dayjs()
                      .add(castingDays + preparationDays + activeDays, "days")
                      .toISOString(),
              );
            },
            reorderProductImages: (oldIndex, newIndex) => {
              set(state => {
                state.campaign.misc.images = arrayMove(state.campaign.misc.images, oldIndex, newIndex);
              });
            },
            addCampaignLocation: location => {
              set(state => {
                state.campaign.misc.campaignLocations.push(location ?? {});
              });
            },
            removeCampaignLocationAt: index => {
              set(state => {
                state.campaign.misc.campaignLocations.splice(index, 1);
              });
            },
            setCampaignLocationAt: (index, location) => {
              set(state => {
                state.campaign.misc.campaignLocations[index] = location;
              });
            },
            resetEventAddress: () => {
              const setCampaignValue = get().actions.misc.setMiscValue;
              setCampaignValue("eventAddressPlaceId", null);
              setCampaignValue("eventAddressStreet", null);
              setCampaignValue("eventAddressPostal", null);
              setCampaignValue("eventAddressCity", null);
              setCampaignValue("eventAddressCountry", null);
              setCampaignValue("eventAddressDescription", "");
            },
            carryOverAddressOnEventToggle: nextEvent => {
              const misc = get().campaign.misc;
              if (Boolean(misc.event) === nextEvent) return;

              if (nextEvent) {
                // Switching to an event: prefill the event address from the on-site location,
                // but never overwrite an event address the brand already entered.
                const location = misc.campaignLocations.find(loc => loc.place_id);
                if (!location || misc.eventAddressPlaceId || misc.eventAddressDescription) return;

                set(state => {
                  state.campaign.misc.eventAddressPlaceId = location.place_id ?? null;
                  state.campaign.misc.eventAddressDescription = location.description ?? "";
                  state.campaign.misc.eventAddressStreet = location.street ?? null;
                  state.campaign.misc.eventAddressPostal = location.postal ?? null;
                  state.campaign.misc.eventAddressCity = location.city ?? null;
                  state.campaign.misc.eventAddressCountry = location.country ?? null;
                });
              } else {
                // Switching away from an event: prefill the on-site location from the event address,
                // but never overwrite an on-site location the brand already entered.
                if (!misc.eventAddressPlaceId && !misc.eventAddressDescription) return;
                if (misc.campaignLocations.some(loc => loc.place_id)) return;

                set(state => {
                  state.campaign.misc.campaignLocations[0] = {
                    city: misc.eventAddressCity ?? null,
                    country: misc.eventAddressCountry ?? null,
                    description: misc.eventAddressDescription ?? "",
                    place_id: misc.eventAddressPlaceId ?? null,
                    postal: misc.eventAddressPostal ?? null,
                    street: misc.eventAddressStreet ?? null,
                  };
                });
              }
            },
            setCreativeSnippets: (
              contentFormat: CreativeSnippetSerializer[],
              moodStyle: CreativeSnippetSerializer[],
            ) => {
              set(state => {
                state.campaign.misc.sortedContentFormatSnippets = contentFormat;
                state.campaign.misc.sortedMoodStyleSnippets = moodStyle;
              });
            },
            setSnippetsLoading: (loading: boolean) => {
              set(state => {
                state.campaign.misc.isLoadingSnippets = loading;
              });
            },
            updateTriggerWordCount: (wordCount: number) => {
              set(state => {
                state.campaign.misc.sortSnippetTriggerWordCount = wordCount;
              });
            },
            getBonusPoolLimit: () => {
              const {campaignType, sellingPrice} = get().campaign.misc;

              if (campaignType === "instagram" || campaignType === "instagram_and_tiktok") {
                return getInstagramBonusPoolLimit(sellingPrice);
              }

              return campaignType === "tiktok" ? BONUS_POOL_MIN_CASH_REWARD : null;
            },
            getWordCount: () => {
              const campaign = get().campaign.misc;
              const description = campaign.longDescription || "";
              const brandDoes = campaign.brandDoes || "";
              const combinedText = `${description} ${brandDoes}`.trim();
              return combinedText.split(/\s+/).filter(word => word.length > 0).length;
            },
            getShouldRefetchSortedSnippets: () => {
              const currentWordCount = get().actions.misc.getWordCount();
              const triggerWordCount = get().campaign.misc.sortSnippetTriggerWordCount;
              const hasNonEmptyCampaignDescription = get().campaign.misc.longDescription.trim().length > 0;

              if (hasNonEmptyCampaignDescription && triggerWordCount === 0) {
                return true;
              }
              if (
                hasNonEmptyCampaignDescription &&
                Math.abs(currentWordCount - triggerWordCount) >= WORD_COUNT_DIFFERENCE_CREATIVE_SNIPPETS_SORTING
              ) {
                return true;
              }
              return false;
            },
          },
          instagram: {
            buildPayload: () => {
              const campaign = get().campaign.instagram;
              const socialCampaign = get().campaign.socials;

              return {
                campaign_size: socialCampaign.campaignSize,
                number_of_feedposts:
                  campaign.requiredContentUploads && "posts" in campaign.requiredContentUploads
                    ? campaign.requiredContentUploads.posts
                    : 0,
                number_of_stories: campaign.requiredContentUploads?.stories ?? 0,
                number_of_reels:
                  campaign.requiredContentUploads && "reels" in campaign.requiredContentUploads
                    ? campaign.requiredContentUploads.reels
                    : 0,
                hashtags: campaign.hashtags.map(tag => tag.replace("#", "")).join(":"),
                instagram: true,
                account_tags: campaign.accountTags.map(tag => tag.replace("@", "")).join(":"),
                task: socialCampaign.task,
                link_sticker: campaign.linkSticker,
                link_sticker_title: campaign.linkStickerTitle,
                instagram_branded_content_ads: campaign.instagramPartnershipAds,
              };
            },
            getNormalizedContentOptionOne: () => {
              const contentOptionOne = get().campaign.instagram.contentOptionOne;

              return contentOptionOne
                ? {
                    stories: contentOptionOne.stories,
                    reels: "reels" in contentOptionOne ? contentOptionOne.reels : 0,
                    posts: "posts" in contentOptionOne ? contentOptionOne.posts : 0,
                  }
                : null;
            },
            getNormalizedContentOptionTwo: () => {
              const contentOptionTwo = get().campaign.instagram.contentOptionTwo;

              return contentOptionTwo
                ? {
                    stories: contentOptionTwo.stories,
                    reels: "reels" in contentOptionTwo ? contentOptionTwo.reels : 0,
                    posts: "posts" in contentOptionTwo ? contentOptionTwo.posts : 0,
                  }
                : null;
            },
            getSelectedRequirementOption: () => {
              const {requiredContentUploads} = get().campaign.instagram;

              const requiredContent = requiredContentUploads
                ? {
                    stories: requiredContentUploads.stories,
                    reels: "reels" in requiredContentUploads ? requiredContentUploads.reels : 0,
                    posts: "posts" in requiredContentUploads ? requiredContentUploads.posts : 0,
                  }
                : null;

              if (isEqual(requiredContent, get().actions.instagram.getNormalizedContentOptionOne())) {
                return "first";
              } else if (isEqual(requiredContent, get().actions.instagram.getNormalizedContentOptionTwo())) {
                return "second";
              } else {
                return null;
              }
            },
            isInstagramWithReelOrPost: () => {
              const campaignType = get().campaign.misc.campaignType;
              const requiredContents = get().campaign.instagram.requiredContentUploads;

              return (
                (campaignType === "instagram" || campaignType === "instagram_and_tiktok") &&
                (requiredContents?.reels! > 0 || requiredContents?.posts! > 0)
              );
            },
            setInstagramValue: (key, value) =>
              set(
                state => {
                  if (
                    key === "requiredContentUploads" &&
                    (get().campaign.misc.cashReward ?? 0) >= BONUS_POOL_MIN_CASH_REWARD
                  ) {
                    const isOldCampaignStoryOnly =
                      get().campaign.instagram.requiredContentUploads?.posts! === 0 &&
                      get().campaign.instagram.requiredContentUploads?.reels! === 0;
                    const isNewCampaignStoryOnly =
                      (value as TContentRequirements)?.posts! === 0 && (value as TContentRequirements)?.reels! === 0;

                    if (!isOldCampaignStoryOnly && isNewCampaignStoryOnly) {
                      state.campaign.misc.isBonusPoolActive = false;
                    }
                  }

                  state.formState.successes = state.formState.successes.filter(
                    success => success !== `instagram-${key}`,
                  );
                  state.formState.warnings = state.formState.warnings.filter(warning => warning !== `instagram-${key}`);

                  state.campaign.instagram[key] = value;
                },
                false,
                "campaignFormStore/setInstagramValue",
              ),
            shouldShowPartnershipAds: () => {
              let data = {} as TContentRequirements;
              let show = false;
              const selectedRequirementOption = get().actions.instagram.getSelectedRequirementOption();

              if (selectedRequirementOption === "first") {
                data = get().actions.instagram.getNormalizedContentOptionOne();
              } else if (selectedRequirementOption === "second") {
                data = get().actions.instagram.getNormalizedContentOptionTwo();
              }

              if (data?.posts! > 0 || data?.reels! > 0) {
                show = true;
              }

              return show;
            },
            changeHashTag: newValue =>
              set(
                state => {
                  const updatedTags: string[] | undefined = getChangedTags("#", newValue);

                  if (updatedTags) {
                    state.campaign.instagram.hashtags = updatedTags;
                    state.formState.warnings = state.formState.warnings.filter(key => key !== "instagram-hashtags");
                    state.formState.successes = state.formState.successes.filter(key => key !== "instagram-hashtags");
                  }
                },
                false,
                "campaignFormStore/changeHashTag",
              ),
            changeAccountTag: newValue =>
              set(
                state => {
                  const updatedTags: string[] | undefined = getChangedTags("@", newValue);

                  if (updatedTags) {
                    state.campaign.instagram.accountTags = updatedTags;
                    state.formState.warnings = state.formState.warnings.filter(key => key !== "instagram-accountTags");
                    state.formState.successes = state.formState.successes.filter(
                      key => key !== "instagram-accountTags",
                    );
                  }
                },
                false,
                "campaignFormStore/changeAccountTag",
              ),
            validateStepTwo: () => {
              set(
                state => {
                  const requiredContentUploads = get().campaign.instagram.requiredContentUploads;

                  if (
                    !requiredContentUploads ||
                    (requiredContentUploads.stories === 0 &&
                      requiredContentUploads.reels === 0 &&
                      requiredContentUploads.posts === 0)
                  ) {
                    state.formState.warnings = [...state.formState.warnings, "instagram-deliverables"];
                  } else {
                    state.formState.warnings = state.formState.warnings.filter(key => key !== "instagram-deliverables");
                    state.formState.successes = [...state.formState.successes, "instagram-deliverables"];
                  }

                  if (get().campaign.instagram.isLinkStickerEnabled) {
                    if (get().campaign.instagram.linkSticker) {
                      state.formState.successes = [...state.formState.successes, "instagram-linkSticker"];
                    } else {
                      state.formState.warnings = [...state.formState.warnings, "instagram-linkSticker"];
                    }
                  }
                },
                false,
                "campaignFormStore/validateStepTwo",
              );

              get().actions.socials.validateStepTwo();

              const result = CampaignInstagramSchema.safeParse(get().campaign.instagram);
              const warningFields = !result.success
                ? result.error.issues.map(issue => `instagram-${issue.path[0].toString()}`)
                : [];

              set(
                state => {
                  state.formState.warnings = [...state.formState.warnings, ...warningFields];
                  state.formState.successes = [
                    ...state.formState.successes,
                    ...Object.keys(CampaignInstagramSchema.shape)
                      .map(fieldKey => `instagram-${fieldKey}`)
                      .filter(key => !warningFields.includes(`instagram-${key}`)),
                  ];
                },
                false,
                "campaignFormStore/validateStepTwo",
              );
            },
          },
          socials: {
            markFieldSuccessIfValid: (key, socialType) => {
              const campaign = get().campaign;

              const selectedTypeSchema = socialType === "instagram" ? CampaignInstagramSchema : CampaignTiktokSchema;

              if (!Object.keys(selectedTypeSchema.shape).includes(key)) {
                return;
              }

              const selectedTypeState = socialType === "instagram" ? campaign.instagram : campaign.tiktok;
              const result = selectedTypeSchema.safeParse(selectedTypeState);

              if (result.success) {
                set(state => {
                  state.formState.successes = [...state.formState.successes, `${socialType}-${key}`];
                });
              }
            },
            setSocialsValue: (key, value) =>
              set(
                state => {
                  state.formState.successes = state.formState.successes.filter(success => success !== key);
                  state.formState.warnings = state.formState.warnings.filter(warning => warning !== key);

                  state.campaign.socials[key] = value;
                },
                false,
                "campaignFormStore/setSocialsValue",
              ),
            validateStepTwo: () => {
              const schemaWithCampaignSizeCondition = useUserStore.getState().user.package_subscription
                ? CampaignSocialSchemaStepTwo.extend({campaignSize: z.string().optional()})
                : CampaignSocialSchemaStepTwo;

              const schema = get().campaign.misc.skipCharacterLimits
                ? schemaWithCampaignSizeCondition.omit({task: true}).extend({task: z.string().min(1, "task_required")})
                : schemaWithCampaignSizeCondition;

              const result = schema.safeParse(get().campaign.socials);
              const warningFields = !result.success ? result.error.issues.map(issue => issue.path[0].toString()) : [];

              set(
                state => {
                  state.formState.warnings = [...state.formState.warnings, ...warningFields];
                  state.formState.successes = [
                    ...state.formState.successes,
                    ...Object.keys(schema.shape).filter(key => !warningFields.includes(key)),
                  ];
                },
                false,
                "campaignFormStore/validateStepTwo",
              );
            },
          },
          tiktok: {
            buildPayload: () => {
              const campaign = get().campaign.tiktok;
              const socialCampaign = get().campaign.socials;
              const accountTags = campaign.accountTags.map(tag => tag?.replace("@", "")).join(":");
              const hashtags = campaign.hashtags.map(tag => tag?.replace("#", "")).join(":");

              const tiktokKeywordPlan = get().actions.tiktok.buildKeywordPlan();

              return {
                campaign_size: socialCampaign.campaignSize,
                tiktok: true,
                task: socialCampaign.task,
                ...(get().campaign.misc.campaignType === "instagram_and_tiktok"
                  ? {tiktok_hashtags: hashtags}
                  : {hashtags}),
                ...(get().campaign.misc.campaignType === "instagram_and_tiktok"
                  ? {tiktok_account_tags: accountTags}
                  : {account_tags: accountTags}),
                tiktok_number_of_medias: campaign.numberOfMedias,
                tiktok_spark_ads: campaign.tiktokSparkAds,
                tiktok_keyword_plan: tiktokKeywordPlan,
              };
            },
            buildKeywordPlan: () => {
              const {keywordPlan} = get().campaign.tiktok;

              return {
                existing_keyword_ids: [...keywordPlan.existing_keyword_ids],
                is_initialized: Boolean(keywordPlan.is_initialized),
                new_keywords: [...keywordPlan.new_keywords],
                ...(keywordPlan.country ? {country: keywordPlan.country} : {}),
                ...(keywordPlan.website_url ? {website_url: keywordPlan.website_url} : {}),
              };
            },
            setTiktokValue: (key, value) =>
              set(
                state => {
                  state.formState.successes = state.formState.successes.filter(success => success !== `tiktok-${key}`);
                  state.formState.warnings = state.formState.warnings.filter(warning => warning !== `tiktok-${key}`);

                  state.campaign.tiktok[key] = value;
                },
                false,
                "campaignFormStore/setTiktokValue",
              ),
            setKeywordSelection: selection =>
              set(
                state => {
                  state.campaign.tiktok.keywordPlan.existing_keyword_ids = selection.existing_keyword_ids;
                  state.campaign.tiktok.keywordPlan.is_initialized = true;
                  state.campaign.tiktok.keywordPlan.new_keywords = selection.new_keywords;
                },
                false,
                "campaignFormStore/setKeywordSelection",
              ),
            clearKeywordPlan: () =>
              set(
                state => {
                  state.campaign.tiktok.keywordPlan.existing_keyword_ids = [];
                  state.campaign.tiktok.keywordPlan.is_initialized = false;
                  state.campaign.tiktok.keywordPlan.new_keywords = [];
                },
                false,
                "campaignFormStore/clearKeywordPlan",
              ),
            setKeywordLimitOverReached: isOverReached =>
              set(
                state => {
                  state.formState.tiktok.isKeywordLimitOverReached = isOverReached;

                  if (!isOverReached) {
                    state.formState.warnings = state.formState.warnings.filter(
                      warning => warning !== "tiktok-keyword-plan",
                    );
                  }
                },
                false,
                "campaignFormStore/setKeywordLimitOverReached",
              ),
            setKeywordPlanCountry: country =>
              set(
                state => {
                  state.campaign.tiktok.keywordPlan.country = country;
                },
                false,
                "campaignFormStore/setKeywordPlanCountry",
              ),
            setKeywordPlanWebsiteUrl: websiteUrl =>
              set(
                state => {
                  state.campaign.tiktok.keywordPlan.website_url = websiteUrl;
                },
                false,
                "campaignFormStore/setKeywordPlanWebsiteUrl",
              ),
            changeHashTag: newValue =>
              set(
                state => {
                  const updatedTags: string[] | undefined = getChangedTags("#", newValue);

                  if (updatedTags) {
                    state.campaign.tiktok.hashtags = updatedTags;
                    state.formState.warnings = state.formState.warnings.filter(key => key !== "tiktok-hashtags");
                    state.formState.successes = state.formState.successes.filter(key => key !== "tiktok-hashtags");
                  }
                },
                false,
                "campaignFormStore/changeHashTag",
              ),
            changeAccountTag: newValue =>
              set(
                state => {
                  const updatedTags: string[] | undefined = getChangedTags("@", newValue);

                  if (updatedTags) {
                    state.campaign.tiktok.accountTags = updatedTags;
                    state.formState.warnings = state.formState.warnings.filter(key => key !== "tiktok-accountTags");
                    state.formState.successes = state.formState.successes.filter(key => key !== "tiktok-accountTags");
                  }
                },
                false,
                "campaignFormStore/changeAccountTag",
              ),
            validateStepTwo: () => {
              get().actions.socials.validateStepTwo();
              const result = CampaignTiktokSchema.safeParse(get().campaign.tiktok);

              const warningFields = !result.success
                ? result.error.issues.map(issue => `tiktok-${issue.path[0].toString()}`)
                : [];

              const keywordLimitWarning = get().formState.tiktok.isKeywordLimitOverReached
                ? ["tiktok-keyword-plan"]
                : [];

              set(
                state => {
                  state.formState.successes = [...state.formState.successes, "tiktok-deliverables"];

                  state.formState.warnings = [...state.formState.warnings, ...warningFields, ...keywordLimitWarning];
                  state.formState.successes = [
                    ...state.formState.successes,
                    ...Object.keys(CampaignInstagramSchema.shape)
                      .map(fieldKey => `tiktok-${fieldKey}`)
                      .filter(key => !warningFields.includes(`tiktok-${key}`)),
                  ];
                },
                false,
                "campaignFormStore/validateStepTwo",
              );
            },
          },
          ugc: {
            buildPayload: () => {
              const campaign = get().campaign.ugc;
              return {
                ugc: true,
                subtitles: campaign.isWithSubtitles,
                source_material: campaign.isSourceMaterialRequested,
                number_of_ugc: campaign.updatedUgcContents.length,
                content_language: campaign.contentLanguage,
                ugc_extra_cash_reward: campaign.extraCashReward ?? 0,
              };
            },
            setUgcValue: (key, value) =>
              set(
                state => {
                  state.formState.warnings = state.formState.warnings.filter(fieldKey => fieldKey !== key);
                  state.formState.successes = state.formState.successes.filter(fieldKey => fieldKey !== key);

                  state.campaign.ugc[key] = value;
                },
                false,
                "campaignFormStore/setUgcValue",
              ),
            initWithExistingUgc: campaign => {
              const isWithSubtitles = campaign.ugc_details?.length
                ? !campaign.ugc_details[0]?.scenes
                    ?.find((scene: {name: string}) => scene.name === "Untertitel")
                    ?.content.includes("nicht erwünscht") || false
                : false;

              const isSourceContentNeeded = campaign.ugc_details?.length
                ? !campaign.ugc_details[0]?.scenes
                    ?.find(scene => scene.name === "Rohmaterial")
                    ?.content.includes("nicht erwünscht") || false
                : false;

              const ugcContents = (campaign.ugc_details ?? []).map((content: TUgcCampaign["ugcContents"][number]) => ({
                id: content.id,
                length: content.length,
                name: content.name,
                scenes: content
                  ?.scenes!.filter(
                    (scene: {name: string}) =>
                      scene.name !== "Untertitel" && scene.name !== "Rohmaterial" && scene.name !== "Sprache",
                  )
                  .map(scene => ({
                    content: scene.content,
                    name: scene.name,
                    place_holder: scene.place_holder,
                  })),
              }));

              return {
                isWithSubtitles,
                isSourceMaterialRequested: isSourceContentNeeded,
                contentLanguage: campaign.content_language,
                ugcContents: ugcContents,
                updatedUgcContents: ugcContents.slice(),
                extraCashReward: campaign.ugc_extra_cash_reward,
                initialCashReward: null,
              };
            },
            saveUgcChanges: async () => {
              const ugcValues = get().campaign.ugc;
              if (isEqual(ugcValues.updatedUgcContents, ugcValues.ugcContents)) return;
              const updatedVideos = ugcValues.updatedUgcContents.filter(ugcContent =>
                ugcValues.ugcContents.find(
                  updatedContent => updatedContent.id === ugcContent.id && !isEqual(updatedContent, ugcContent),
                ),
              );
              await Promise.all([
                ...updatedVideos.map((ugcContent, idx) =>
                  client.post(CAMPAIGN_KEYS.mCampaignUgcDetailsUpdate[0], {
                    length: ugcContent.length,
                    name: "Video " + (idx + 1),
                    scenes: ugcContent.scenes,
                    ugc_content_id: ugcContent.id,
                  }),
                ),
              ]);
            },
            addUgcVideo: async (campaignId, length) => {
              const response = await client.post<UGCContentSerializer>(
                CAMPAIGN_KEYS.mCampaignUgcContent(campaignId || get().campaign.misc.id!)[0],
              );

              const newVideo = {
                ...response.data,
                length: length ?? response.data.length,
              };

              set(
                state => {
                  state.campaign.ugc.updatedUgcContents.push(newVideo);
                  state.campaign.ugc.ugcContents.push(newVideo);
                },
                false,
                "campaignFormStore/addUgcVideo",
              );
              get().actions.ugc.calculateInitialCashReward();
            },
            deleteUgcVideo: async index => {
              const ugcContent = get().campaign.ugc.updatedUgcContents[index];
              if (ugcContent.id) {
                await client.post(CAMPAIGN_KEYS.mCampaignUgcContentRemoval[0], {ugc_content_id: ugcContent.id});
              }
              set(
                state => {
                  state.campaign.ugc.updatedUgcContents.splice(index, 1);
                },
                false,
                "campaignFormStore/deleteUgcVideo",
              );
              get().actions.ugc.calculateInitialCashReward();
            },
            addUgcScene: index =>
              set(
                state => {
                  state.campaign.ugc.updatedUgcContents[index].scenes!.push({
                    content: "",
                    is_added_by_admin: true,
                    name: "",
                    place_holder: "",
                  });
                },
                false,
                "campaignFormStore/addUgcScene",
              ),
            deleteUgcScene: (videoIndex, sceneIndex) =>
              set(
                state => {
                  state.campaign.ugc.updatedUgcContents[videoIndex].scenes!.splice(sceneIndex, 1);
                },
                false,
                "campaignFormStore/deleteUgcScene",
              ),
            changeUgcScene: (videoIndex, sceneIndex, key, value) =>
              set(
                state => {
                  state.campaign.ugc.updatedUgcContents[videoIndex].scenes![sceneIndex][key] = value;
                },
                false,
                "campaignFormStore/changeUgcScene",
              ),
            changeUgcVideo: (index, key, value) => {
              set(
                state => {
                  state.campaign.ugc.updatedUgcContents[index][key] = value;
                },
                false,
                "campaignFormStore/changeUgcVideo",
              );
              get().actions.ugc.calculateInitialCashReward();
            },
            calculateInitialCashReward: () => {
              const reward = sumBy(get().campaign.ugc.updatedUgcContents, content => {
                if (content.length === 30) return UGC_COST_PER_VIDEO_30_SECONDS * UGC_CASH_REWARD_PERCENTAGE;
                else if (content.length === 15) return UGC_COST_PER_VIDEO_15_SECONDS * UGC_CASH_REWARD_PERCENTAGE;
                else return 0;
              });

              set(state => {
                state.campaign.ugc.initialCashReward = Math.floor(reward);
              });
            },
          },
        },
      }),
      {
        name: "Campaign Form Store",
      },
    ),
  ),
);

export type TInstagramCampaign = TComposedCampaign["misc"] & TComposedCampaign["instagram"];
export type TTiktokCampaign = TComposedCampaign["misc"] & TComposedCampaign["tiktok"];
export type TUgcCampaign = TComposedCampaign["misc"] & TComposedCampaign["ugc"];

export function useComposedInstagramCampaignState(): TInstagramCampaign;
export function useComposedInstagramCampaignState<TReturnType>(
  selector: (state: TInstagramCampaign) => TReturnType,
): TReturnType;

export function useComposedInstagramCampaignState<TReturnType>(selector?: (state: TInstagramCampaign) => TReturnType) {
  return useCampaignFormStore(
    useShallow(state =>
      selector
        ? selector({...state.campaign.misc, ...state.campaign.instagram})
        : ({...state.campaign.misc, ...state.campaign.instagram} as TReturnType),
    ),
  );
}

export function useComposedTiktokCampaignState(): TTiktokCampaign;
export function useComposedTiktokCampaignState<TReturnType>(
  selector: (state: TTiktokCampaign) => TReturnType,
): TReturnType;

export function useComposedTiktokCampaignState<TReturnType>(selector?: (state: TTiktokCampaign) => TReturnType) {
  return useCampaignFormStore(
    useShallow(state =>
      selector
        ? selector({...state.campaign.misc, ...state.campaign.tiktok})
        : ({...state.campaign.misc, ...state.campaign.tiktok} as TReturnType),
    ),
  );
}

export function useComposedUgcCampaignState(): TUgcCampaign;
export function useComposedUgcCampaignState<TReturnType>(selector: (state: TUgcCampaign) => TReturnType): TReturnType;

export function useComposedUgcCampaignState<TReturnType>(selector?: (state: TUgcCampaign) => TReturnType) {
  return useCampaignFormStore(
    useShallow(state =>
      selector
        ? selector({...state.campaign.misc, ...state.campaign.ugc})
        : ({...state.campaign.misc, ...state.campaign.ugc} as TReturnType),
    ),
  );
}

export function useComposedCampaignState(): TComposedCampaign;
export function useComposedCampaignState<TReturnType>(selector: (state: TComposedCampaign) => TReturnType): TReturnType;

export function useComposedCampaignState<TReturnType>(selector?: (state: TComposedCampaign) => TReturnType) {
  return useCampaignFormStore(
    useShallow(state => (selector ? selector(state.campaign) : (state.campaign as TReturnType))),
  );
}

export const useSetMiscValue = () => useCampaignFormStore(state => state.actions.misc.setMiscValue);

export const useSetInstagramValue = () => useCampaignFormStore(state => state.actions.instagram.setInstagramValue);

export const useSetSocialsValue = () => useCampaignFormStore(state => state.actions.socials.setSocialsValue);

export const useSetTiktokValue = () => useCampaignFormStore(state => state.actions.tiktok.setTiktokValue);

export const useSetUgcValue = () => useCampaignFormStore(state => state.actions.ugc.setUgcValue);

export const useSetCampaignFormState = () => useCampaignFormStore(state => state.actions.misc.setFormState);
export const useUpdateScheduling = () => useCampaignFormStore(state => state.actions.misc.updateScheduling);

export function useCampaignFormState(): TFormState;
export function useCampaignFormState<TReturnType>(selector: (state: TFormState) => TReturnType): TReturnType;

export function useCampaignFormState<TReturnType>(selector?: (state: TFormState) => TReturnType) {
  return useCampaignFormStore(state => (selector ? selector(state.formState) : (state.formState as TReturnType)));
}

export function useCampaignFormActions(): TActions;
export function useCampaignFormActions<TReturnType>(selector: (state: TActions) => TReturnType): TReturnType;

export function useCampaignFormActions<TReturnType>(selector?: (state: TActions) => TReturnType) {
  return useCampaignFormStore(state => (selector ? selector(state.actions) : (state.actions as TReturnType)));
}

export const useInstagramActions = () => useCampaignFormActions().instagram;
export const useTiktokActions = () => useCampaignFormActions().tiktok;
export const useUgcActions = () => useCampaignFormActions().ugc;
export const useMiscActions = () => useCampaignFormActions().misc;
